import { useMutation } from '@apollo/client';
import axios from 'axios';
import { useCallback } from 'react';
import { slugify } from 'transliteration';

import { UploadApplication } from '@xing-com/crate-common-graphql-types';
import type { UploadRequestMutation } from '@xing-com/crate-jobs-graphql';
import { UploadRequestDocument } from '@xing-com/crate-jobs-graphql';

const UPLOAD_URI = '/upload/file';

export type RequestedUpload = NonNullable<
  NonNullable<UploadRequestMutation['uploadRequest']>['success']
>;

type UploadFileOptions = {
  signal: AbortSignal;
  onUploadRequested?: (upload: RequestedUpload) => void;
  onUploadProgress?: (progress: number, upload: RequestedUpload) => void;
};

type FileServerUploadResult = {
  upload: RequestedUpload;
  status?: number;
};

type UseFileServerUploadResult = {
  uploadFile: (
    file: File,
    options: UploadFileOptions
  ) => Promise<FileServerUploadResult>;
  deleteFile: (
    upload: Pick<RequestedUpload, 'id' | 'authToken'>
  ) => Promise<void>;
};

export const useFileServerUpload = (): UseFileServerUploadResult => {
  const [uploadRequest] = useMutation(UploadRequestDocument);

  const uploadFile = useCallback(
    async (
      file: File,
      { signal, onUploadRequested, onUploadProgress }: UploadFileOptions
    ): Promise<FileServerUploadResult> => {
      const { data } = await uploadRequest({
        variables: {
          input: {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            application: UploadApplication.Jobs,
          },
        },
      });
      const upload = data?.uploadRequest?.success;

      if (!upload) {
        throw new Error('Error requesting file upload');
      }

      if (signal.aborted) {
        return { upload };
      }

      onUploadRequested?.(upload);

      const response = await axios.patch(`${UPLOAD_URI}/${upload.id}`, file, {
        signal,
        headers: {
          Authorization: `Bearer ${upload.authToken}`,
          'Upload-Offset': '0',
          'Tus-Resumable': '1.0.0',
          'Content-Type': 'application/offset+octet-stream',
          'Content-Disposition': `attachment; filename="${slugify(file.name)}"`,
          'Upload-Metadata': `content-type="${slugify(file.type)}"`,
        },
        onUploadProgress: onUploadProgress
          ? ({ loaded, total }) => {
              if (total !== undefined && !signal.aborted) {
                onUploadProgress(Math.floor((loaded / total) * 100), upload);
              }
            }
          : undefined,
      });

      return { upload, status: response.status };
    },
    [uploadRequest]
  );

  const deleteFile = useCallback(
    async ({
      id,
      authToken,
    }: Pick<RequestedUpload, 'id' | 'authToken'>): Promise<void> => {
      await axios.delete(`${UPLOAD_URI}/${id}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Tus-Resumable': '1.0.0',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
    },
    []
  );

  return { uploadFile, deleteFile };
};
